import axios from "axios";
import axiosInstance from "./axios-service/axiosInstance";
import microsoftGraphInstance from "./axios-service/microsoftGraphInstance";
import dayjs from "dayjs";

const headers = {
  Accept: "application/json;odata=nometadata",
  Prefer: "odata.include-annotations=*",
};

const isLive =
  typeof window !== "undefined" &&
  window.location.hostname === "talenthub.intelegain.com";

const interviewRoundService = {
  fetchAllInterviewRounds: (filterBy = "") => {
    const filterQuery = filterBy
      ? `&$filter=_itl_interviewer_value eq ${filterBy}`
      : "";

    return axiosInstance.get(
      `/itl_interviewrounds2s?$select=_itl_candidateid_value,itl_eventid,itl_duration,itl_meeting_url,itl_interviewdatetime,_itl_interviewer_value,_itl_requisitionid_value,itl_status,itl_mode,itl_isinterviewroundskipped${filterQuery}`,
      { headers }
    );
  },

  fetchInterviewRoundbyCandidateIDReqID: (reqID, canID) => {
    return axiosInstance.get(
      `/itl_interviewrounds2s?$select=itl_feedbackchangedby,itl_feedbackupdatedate, _itl_candidateid_value,itl_feedback,itl_interviewdatetime,itl_isinterviewroundskipped,itl_duration,_itl_interviewer_value,itl_mode,_itl_requisitionid_value,itl_status&$expand=itl_CandidateID($select=itl_candidatecontact,lastname,itl_candidatename,itl_candidatestatus,itl_emailid,_itl_locationid_value)&$filter=(_itl_requisitionid_value eq ${reqID} and _itl_candidateid_value eq ${canID})&$orderby=createdon asc`,
      {
        headers: headers,
      }
    );
  },

  createInterviewRound: (obj) => {
    return axiosInstance.post("/itl_interviewrounds2s", obj, {
      headers: {
        ...headers,
        Prefer: "return=representation, odata.include-annotations=*",
      },
    });
  },

  editInterviewRound: (id, obj) => {
    return axiosInstance.patch(`/itl_interviewrounds2s(${id})`, obj, {
      headers: {
        ...headers,
        Prefer: "return=representation, odata.include-annotations=*",
      },
    });
  },

  createTeamsMeetingAPI: async (reqBody, token) => {
    try {
      const response = await axios.post(
        "https://graph.microsoft.com/v1.0/me/events",
        reqBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data; // Return the created meeting details
    } catch (error) {
      console.error(
        "Error creating Teams meeting:",
        error.response?.data || error.message
      );
      throw error; // Re-throw error for handling in the calling function
    }
  },

  sendOutlookEmail: async (reqBody, token) => {
    try {
      const response = await axios.post(
        "https://graph.microsoft.com/v1.0/me/sendMail",
        reqBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error(
        "Error sending email:",
        error.response ? error.response.data : error.message
      );
      return false;
    }
  },

  // Interview Status Update Email Services
  fetchAllInterviewStatusEmailSent: async () => {
    return axiosInstance.get(
      "/itl_emails?$select=itl_body,itl_emailbcc,itl_emailcc,itl_emailto,itl_sentby,itl_subject&$orderby=createdon desc",
      {
        headers: headers,
      }
    );
  },

  fetchEmailByReqIDCanID: async (reqID, canID) => {
    return axiosInstance.get(
      `/itl_emails?$select=itl_body,itl_emailbcc,itl_emailcc,itl_emailto,itl_interviewround,itl_interviewstatus,itl_sentby,itl_subject&$filter=(_itl_requisitionid_value eq ${reqID} and _itl_candidateid_value eq ${canID})&$orderby=createdon desc`,
      {
        headers: headers,
      }
    );
  },

  addInterviewStatusEmailSent: async (obj) => {
    return axiosInstance.post("/itl_emails", obj, {
      headers: headers,
    });
  },

  fetchMeetingInfoTest: async (id, joinWebURL) => {
    try {
      const encodedUrl = encodeURIComponent(joinWebURL);
      // const meetingSchedule = microsoftGraphInstance.get(
      //   `/me/onlineMeetings/${id}`
      // );

      const response = await microsoftGraphInstance.get(
        `/me/onlineMeetings?$filter=JoinWebUrl eq '${encodedUrl}'`
      );

      const meetingURL = await microsoftGraphInstance.get("/");
    } catch (error) {
      console.error("Error fetching Meeting Info: ");
    }
  },

  fetchMeetingInfo: async (id, joinWebURL) => {
    try {
      const encodedUrl = encodeURIComponent(joinWebURL);

      // Get meeting details by join URL
      const response = await microsoftGraphInstance.get(
        `/me/onlineMeetings?$filter=JoinWebUrl eq '${joinWebURL}'`
      );

      const meetings = response?.data?.value;
      if (!meetings || meetings.length === 0) {
        throw new Error("Meeting not found");
      }

      const meeting = meetings[0];

      // Usually, the recording URL is posted in the chat or stored in OneDrive/SharePoint
      // You'd need to list chat messages to find the recording URL
      const chatId = meeting.chatInfo?.threadId;

      if (!chatId) {
        throw new Error("Chat thread ID not found for the meeting");
      }

      const chatMessages = await microsoftGraphInstance.get(
        `/chats/${chatId}/messages`
      );

      const messages = chatMessages?.data?.value || [];

      // Try to find a message with a video or recording URL
      const recordingMessage = messages.find(
        (msg) =>
          msg.body?.content?.includes("https://") &&
          msg.body?.content?.includes("teams.microsoft.com") &&
          msg.body?.content?.includes("video")
      );

      let recordingURL = null;

      if (recordingMessage) {
        const urlMatch =
          recordingMessage.body.content.match(/https:\/\/[^\s'"]+/);
        recordingURL = urlMatch ? urlMatch[0] : null;
      }

      return {
        meetingId: meeting.id,
        subject: meeting.subject,
        recordingURL: recordingURL || null,
      };
    } catch (error) {
      console.error("Error fetching Meeting Info: ", error.message);
      return null;
    }
  },

  getCandidatePastInterviews: async (id) => {
    return axiosInstance.get(
      `/itl_interviewrounds2s?$select=itl_feedback,itl_mode,itl_interviewdatetime,itl_location,_itl_requisitionid_value,itl_status&$expand=itl_RequisitionID($select=itl_department,itl_name)&$filter=_itl_candidateid_value eq ${id}&$orderby=createdon desc`
    );
  },

  addInterviewNotes: async (obj) => {
    return axiosInstance.post("/itl_interviewnotes", obj, {
      headers: headers,
    });
  },

  getInterviewNotes: async (candidate_Id, requisition_Id) => {
    return axiosInstance.get(
      `/itl_interviewnotes?$select=itl_user,itl_notetext,createdon,modifiedon&$orderby=createdon asc&$filter=(itl_candidateid eq ${candidate_Id} and itl_requisitionid eq ${requisition_Id})`
    );
  },

  updateInterviewNotes: (note) => {
    const { itl_interviewnoteid } = note;
    return axiosInstance.patch(
      `/itl_interviewnotes(${itl_interviewnoteid})`,
      note
    );
  },

  fetchAllInterviewedCandidates: () => {
    return axiosInstance.get(
      "/itl_interviewrounds2s?$select=_itl_candidateid_value,itl_interviewdatetime,itl_status,_itl_requisitionid_value&$expand=itl_CandidateID($select=itl_candidatecontact,lastname,itl_location,_itl_locationid_value),itl_RequisitionID($select=itl_name)&$orderby=createdon desc",
      {
        headers: headers,
      }
    );
  },

  fetchAllTeamEvents: (token) => {
    const startDateTime = dayjs().startOf("year").toISOString();
    const endDateTime = dayjs().endOf("year").toISOString();
    const url = `https://graph.microsoft.com/v1.0/me/calendar/calendarView?startDateTime=${startDateTime}&endDateTime=${endDateTime}&$top=999`;
    return axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  sendFeedbackCard: async (payload) => {
    const API_URL = isLive
      ? "https://317b454404b6e0ec8040763a25d1ae.d9.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/f29f558f4cd94c3db3e28d69bf7ed0f3/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=K3mxdBX3KAuzek7IlFs8SyyX04NTfkA3Sv-VcbpZ8RM"
      : "https://e6bcb97abef1e33a9b36ba1c392e51.de.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/eccab2a07c5249cfb9fa395d568e11b4/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=DcnvDDQnuiimdUDLzjVVFVI1pERbHZ_pDmFJKh5rPg4";

    await axios.post(API_URL, payload, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
        Prefer: "return=representation, odata.include-annotations=*",
      },
    });
  },
};

export default interviewRoundService;
